import {
  createSessionResponseSchema,
  healthResponseSchema,
  pollCommandsResponseSchema,
  type CreateSessionResponse,
  type PollCommandsResponse,
  type SubmitToolResult,
} from '@app/platform/ai-session-protocol';

export class AiSessionRelayError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AiSessionRelayError';
  }
}

export class AiSessionRelayClient {
  async checkHealth(baseUrl: string): Promise<void> {
    const response = await fetch(this.url(baseUrl, '/api/ai/health'), {
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
    healthResponseSchema.parse(await this.readJson(response));
  }

  async createSession(
    baseUrl: string,
    sessionKey: string,
    mcpKey: string,
    paired = false,
  ): Promise<CreateSessionResponse> {
    const response = await fetch(this.url(baseUrl, '/api/ai/sessions'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ sessionKey, mcpKey, paired }),
    });
    return createSessionResponseSchema.parse(await this.readJson(response));
  }

  async poll(baseUrl: string, sessionKey: string, signal: AbortSignal): Promise<PollCommandsResponse | null> {
    const response = await fetch(this.url(baseUrl, '/api/ai/poll'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ sessionKey }),
      signal,
    });
    if (response.status === 204) return null;
    return pollCommandsResponseSchema.parse(await this.readJson(response));
  }

  async submitResult(baseUrl: string, sessionKey: string, result: SubmitToolResult): Promise<void> {
    const response = await fetch(this.url(baseUrl, '/api/ai/results'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ sessionKey, ...result }),
    });
    if (!response.ok) {
      await this.readJson(response);
    }
  }

  async closeSession(baseUrl: string, sessionKey: string): Promise<void> {
    const response = await fetch(this.url(baseUrl, '/api/ai/sessions'), {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ sessionKey }),
    });
    if (!response.ok) {
      await this.readJson(response);
    }
  }

  private url(baseUrl: string, path: string): string {
    return `${baseUrl.replace(/\/$/, '')}${path}`;
  }

  private async readJson(response: Response): Promise<unknown> {
    const body = await response.json().catch(() => undefined);
    if (!response.ok) {
      const message =
        typeof body === 'object' &&
        body !== null &&
        'error' in body &&
        typeof body.error === 'object' &&
        body.error !== null &&
        'message' in body.error &&
        typeof body.error.message === 'string'
          ? body.error.message
          : `Relay request failed with HTTP ${response.status}`;
      throw new AiSessionRelayError(response.status, message);
    }
    return body;
  }
}
